'use client'

import { useMemo } from 'react'
import { useTranslations } from '@repo/core/i18n'
import { Card } from '@repo/ui/card'
import { Badge } from '@repo/ui/badge'
import { Button } from '@repo/ui/button'
import { CalendarRegular as CalendarIcon, ClockRegular, DrinkCoffeeRegular, ErrorCircleRegular } from '@fluentui/react-icons'
import type {
  CrmMemberSchedule,
  MemberDaySchedule,
  MemberTimeSlot,
  CrmBreakSchedule,
  CrmScheduleException,
} from '../../entities/member/model/schedule-types'

interface ScheduleCalendarProps {
  schedule: CrmMemberSchedule
  onEditShift?: (dayOfWeek: number, shift: MemberTimeSlot) => void
  onAddShift?: (dayOfWeek: number) => void
  onRemoveShift?: (dayOfWeek: number, shiftIndex: number) => void
  onEditBreak?: (breakSchedule: CrmBreakSchedule) => void
  readOnly?: boolean
}

const HOURS = Array.from({ length: 24 }, (_, i) => i)

export function ScheduleCalendar({
  schedule,
  onEditShift,
  onAddShift,
  onRemoveShift,
  onEditBreak,
  readOnly = false,
}: ScheduleCalendarProps) {
  const t = useTranslations('crm.scheduleCalendar')
  // Get active exceptions for the current week
  const activeExceptions = useMemo(() => {
    const now = new Date()
    const weekStart = new Date(now)
    weekStart.setDate(now.getDate() - now.getDay() + 1) // Monday
    const weekEnd = new Date(weekStart)
    weekEnd.setDate(weekStart.getDate() + 6) // Sunday

    return schedule.exceptions.filter((exc) => {
      const excStart = new Date(exc.startDate)
      const excEnd = new Date(exc.endDate)
      return (
        exc.isApproved &&
        ((excStart >= weekStart && excStart <= weekEnd) ||
          (excEnd >= weekStart && excEnd <= weekEnd) ||
          (excStart <= weekStart && excEnd >= weekEnd))
      )
    })
  }, [schedule.exceptions])

  // Get day schedule for a specific day
  const getDaySchedule = (dayOfWeek: number): MemberDaySchedule | undefined => {
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const
    const dayName = dayNames[dayOfWeek]
    if (!dayName) return undefined
    return schedule.weeklySchedule[dayName]
  }

  // Check if a time falls within a break
  const isTimeInBreak = (dayOfWeek: number, hour: number): CrmBreakSchedule | undefined => {
    return schedule.breaks.find((breakSchedule) => {
      if (breakSchedule.daysOfWeek && !breakSchedule.daysOfWeek.includes(dayOfWeek)) {
        return false
      }
      const breakStart = parseInt(breakSchedule.startTime.split(':')[0] ?? '0')
      const breakEnd = parseInt(breakSchedule.endTime.split(':')[0] ?? '0')
      return hour >= breakStart && hour < breakEnd
    })
  }

  // Check if a day has an exception
  const getDayException = (dayOfWeek: number): CrmScheduleException | undefined => {
    const date = new Date()
    date.setDate(date.getDate() - date.getDay() + dayOfWeek + 1) // Adjust to match week
    const dateString = date.toISOString().split('T')[0] ?? ''

    return activeExceptions.find((exc) => {
      return dateString >= exc.startDate && dateString <= exc.endDate
    })
  }

  // Format time for display
  const formatTime = (time: string): string => {
    const parts = time.split(':')
    return `${parts[0] ?? '00'}:${parts[1] ?? '00'}`
  }

  // Get exception badge color
  const getExceptionColor = (type: CrmScheduleException['type']): string => {
    switch (type) {
      case 'vacation':
        return 'bg-info/20 text-info-foreground'
      case 'sick_leave':
        return 'bg-destructive/20 text-destructive-foreground'
      case 'training':
        return 'bg-success/20 text-success-foreground'
      case 'meeting':
        return 'bg-warning/20 text-warning-foreground'
      default:
        return 'bg-muted/50 text-foreground'
    }
  }

  const daysOfWeek = [
    t('days.monday'),
    t('days.tuesday'),
    t('days.wednesday'),
    t('days.thursday'),
    t('days.friday'),
    t('days.saturday'),
    t('days.sunday'),
  ]

  const shortDaysOfWeek = [
    t('daysShort.monday'),
    t('daysShort.tuesday'),
    t('daysShort.wednesday'),
    t('daysShort.thursday'),
    t('daysShort.friday'),
    t('daysShort.saturday'),
    t('daysShort.sunday'),
  ]

  // Render a single day column
  const renderDayColumn = (dayOfWeek: number) => {
    const daySchedule = getDaySchedule(dayOfWeek)
    const exception = getDayException(dayOfWeek)

    return (
      <div key={dayOfWeek} className="flex flex-col border-r last:border-r-0">
        {/* Day header */}
        <div className="border-b bg-muted p-2 text-center">
          <div className="text-sm font-medium">{daysOfWeek[dayOfWeek]}</div>
          {exception && (
            <Badge className={`mt-1 text-xs ${getExceptionColor(exception.type)}`}>
              {t(`exceptions.types.${exception.type}`)}
            </Badge>
          )}
        </div>

        {/* Time grid */}
        <div className="flex-1">
          {HOURS.map((hour) => {
            const isWorkHour = daySchedule?.shifts.some((shift) => {
              const startHour = parseInt(shift.start.split(':')[0] ?? '0')
              const endHour = parseInt(shift.end.split(':')[0] ?? '0')
              return hour >= startHour && hour < endHour
            })

            const breakSchedule = isTimeInBreak(dayOfWeek, hour)

            return (
              <div
                key={hour}
                className={`border-b p-1 text-center text-xs ${
                  exception
                    ? 'bg-muted/30 opacity-50'
                    : breakSchedule
                      ? 'bg-warning/10'
                      : isWorkHour
                        ? 'bg-success/10'
                        : 'bg-background'
                }`}
                style={{ height: '40px' }}
              >
                {breakSchedule && hour === parseInt(breakSchedule.startTime.split(':')[0] ?? '0') && (
                  <div
                    className="flex cursor-pointer items-center justify-center gap-1 text-warning"
                    onClick={() => !readOnly && onEditBreak?.(breakSchedule)}
                  >
                    <DrinkCoffeeRegular className="h-3 w-3" />
                    <span>{t(`breaks.types.${breakSchedule.type}`)}</span>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Shifts summary */}
        {daySchedule?.isWorkDay && !exception && (
          <div className="border-t bg-muted p-2">
            {daySchedule.shifts.map((shift, index) => (
              <div key={index} className="mb-1 flex items-center justify-between text-xs">
                <div className="flex items-center gap-1">
                  <ClockRegular className="h-3 w-3" />
                  <span>
                    {formatTime(shift.start)} - {formatTime(shift.end)}
                  </span>
                </div>
                {!readOnly && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-5 px-1"
                    onClick={() => onRemoveShift?.(dayOfWeek, index)}
                  >
                    ×
                  </Button>
                )}
              </div>
            ))}
            {!readOnly && (
              <Button
                size="sm"
                variant="outline"
                className="mt-1 h-6 w-full text-xs"
                onClick={() => onAddShift?.(dayOfWeek)}
              >
                {t('actions.addShift')}
              </Button>
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Exceptions list */}
      {activeExceptions.length > 0 && (
        <Card className="p-4">
          <div className="mb-2 flex items-center gap-2 text-sm font-medium">
            <ErrorCircleRegular className="h-4 w-4 text-warning" />
            <span>{t('exceptions.activeThisWeek')}</span>
          </div>
          <div className="space-y-2">
            {activeExceptions.map((exception) => (
              <div
                key={exception.id}
                className="flex items-center justify-between rounded border p-2"
              >
                <div className="flex items-center gap-2">
                  <Badge className={getExceptionColor(exception.type)}>
                    {t(`exceptions.types.${exception.type}`)}
                  </Badge>
                  <span className="text-sm">
                    {new Date(exception.startDate).toLocaleDateString(undefined)} -{' '}
                    {new Date(exception.endDate).toLocaleDateString(undefined)}
                  </span>
                  {exception.reason && (
                    <span className="text-xs text-muted-foreground">({exception.reason})</span>
                  )}
                </div>
                {!exception.isApproved && (
                  <Badge variant="outline" className="text-xs">
                    {t('exceptions.pendingApproval')}
                  </Badge>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Calendar grid */}
      <Card className="overflow-hidden">
        <div className="flex">
          {/* Time labels */}
          <div className="flex w-16 flex-col border-r bg-muted">
            <div className="border-b p-2 text-center text-sm font-medium">{t('labels.time')}</div>
            <div className="flex-1">
              {HOURS.map((hour) => (
                <div
                  key={hour}
                  className="border-b p-1 text-center text-xs"
                  style={{ height: '40px' }}
                >
                  {hour.toString().padStart(2, '0')}:00
                </div>
              ))}
            </div>
          </div>

          {/* Day columns */}
          <div className="flex flex-1">
            {[0, 1, 2, 3, 4, 5, 6].map((dayOfWeek) => renderDayColumn(dayOfWeek))}
          </div>
        </div>
      </Card>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 rounded bg-success/10 border" />
          <span>{t('legend.workHours')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 rounded bg-warning/10 border" />
          <span>{t('legend.breakTime')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 rounded bg-muted/30 border" />
          <span>{t('legend.exception')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 rounded bg-background border" />
          <span>{t('legend.offHours')}</span>
        </div>
      </div>

      {/* Breaks summary */}
      {schedule.breaks.length > 0 && (
        <Card className="p-4">
          <div className="mb-2 flex items-center gap-2 text-sm font-medium">
            <DrinkCoffeeRegular className="h-4 w-4" />
            <span>{t('breaks.title')}</span>
          </div>
          <div className="space-y-2">
            {schedule.breaks.map((breakSchedule) => (
              <div
                key={breakSchedule.id}
                className="flex items-center justify-between rounded border p-2"
              >
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{t(`breaks.types.${breakSchedule.type}`)}</Badge>
                  <span className="text-sm">
                    {formatTime(breakSchedule.startTime)} - {formatTime(breakSchedule.endTime)}
                  </span>
                  {breakSchedule.daysOfWeek && (
                    <span className="text-xs text-muted-foreground">
                      ({breakSchedule.daysOfWeek.map((d) => shortDaysOfWeek[d] ?? '').join(', ')})
                    </span>
                  )}
                </div>
                {!readOnly && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onEditBreak?.(breakSchedule)}
                  >
                    {t('actions.edit')}
                  </Button>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Timezone info */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <CalendarIcon className="h-4 w-4" />
        <span>{t('labels.timezone', { timezone: schedule.timezone })}</span>
      </div>
    </div>
  )
}
